import React from "react"
import { Box, CircularProgress, Theme, Typography, createStyles, makeStyles } from "@material-ui/core"
import { useAppContext } from "../../../app-context/use-app-context"
import ApprovedHoursTable from "../../../treegrid/approved-hours"
import { useCalendarSettings } from "../../../hooks/use-calendar-settings"
import { useConfirmDialog } from "../../../components/ConfirmDialog"
import {
  GetHourReportDayAuditsQuery,
  GetReportedHoursQuery,
  useGetHourReportDayAuditsQuery,
  useGetReportedHoursQuery,
} from "../../api"
import { makeReportedHoursScopeQuery } from "../../api/reported-hours-query"
import { useHourReportMutations } from "../../hooks/use-hour-report-mutations"
import { useGetOrgHourReportAuditableMembershipsQuery } from "../../../memberships/api"
import DateRangeSelector from "../DateRangeSelector/DateRangeSelector"
import { formatDateRangeQuery, useDateRange } from "../DateRangeSelector/use-date-range"
import ReporterMembershipFilter from "../ReporterMembershipFilter"
import AddHourReportDialog from "./AddHourReportDialog"
import HourReportCorrectionForm from "./HourReportCorrectionForm"
import { getTableHeaderText } from "../utils"
import type { HourReportingWorkflowSettingsProps } from "../../hooks/use-hour-reporting-workflow-settings"
import { resolveDefaultMembership } from "../../resolve-membership"
import { useAuthUserMembership } from "../../../memberships/hooks/use-auth-user-membership"
import { useLocalizedFormat } from "../../../hooks/use-localized-format"
import { useTranslation } from "react-i18next"
import { useHourReportMembershipQuery } from "../../hooks/use-hour-report-membership-query"

const ApproveHours = ({ workflowSettings }: HourReportingWorkflowSettingsProps) => {
  const classes = useStyles()
  const formatDate = useLocalizedFormat()
  const { t } = useTranslation("hour-report")

  const { appContext } = useAppContext()
  const isUserContext = appContext.mainContext?.type === "user"
  const isOrganisationContext = appContext.mainContext?.type === "org"
  const {
    membership: authUserMembership,
    canReportOthersHoursAnywhere,
    canReportOthersHoursAsMember,
  } = useAuthUserMembership()
  const canReportOthersHours = Boolean(canReportOthersHoursAnywhere || canReportOthersHoursAsMember)
  const defaultMembershipId =
    resolveDefaultMembership({
      subContext: appContext.subContext,
      authUserMembership,
      canReportOthersHours,
    })?.id || null
  const orgId = appContext.mainContext?.type === "org" ? appContext.mainContext.id : ""
  const { data: memberships = [], isLoading: isLoadingMemberships } = useGetOrgHourReportAuditableMembershipsQuery(
    orgId,
    { skip: !orgId }
  )
  const { selectedMembershipId, setSelectedMembershipId } = useHourReportMembershipQuery({
    defaultMembershipId,
    membershipIds: memberships.map(({ id }) => id),
    isLoading: isLoadingMemberships,
    allowEmpty: true,
  })

  const { calendarSettings, isLoading: isLoadingCalendarSettings } = useCalendarSettings({
    mainContext: appContext.mainContext,
    skipAll: !appContext.mainContext,
  })

  const { fromDate, toDate, setFromDate, setToDate, setDateRange } = useDateRange()
  const hourReportQueryParams = useApproveHoursQueryParams({
    fromDate,
    toDate,
    appContext,
    membershipId: selectedMembershipId,
  })
  const { data: reports = [], isLoading: isLoadingReports } = useGetReportedHoursQuery(hourReportQueryParams, {
    refetchOnMountOrArgChange: true,
  })

  const dayAuditsQueryParams = useDayAuditsQueryParams({
    fromDate,
    toDate,
    appContext,
    membershipId: selectedMembershipId,
  })
  const { data: dayAudits = [], isLoading: isLoadingDayAudits } = useGetHourReportDayAuditsQuery(dayAuditsQueryParams, {
    skip: !selectedMembershipId,
    refetchOnMountOrArgChange: true,
  })

  const [addHourReportDate, setAddHourReportDate] = React.useState<Date | null>(null)
  const [editingReportId, setEditingReportId] = React.useState<string | null>(null)
  const editingReport = reports.find((report) => report.id === editingReportId)
  const { deleteHourReport } = useHourReportMutations()
  const { confirm: handleDeleteHourReport } = useConfirmDialog({
    title: t("dialogs.deleteTitle", { defaultValue: "Delete Hour Report" }),
    text: t("dialogs.deleteText", { defaultValue: "Are you sure you want to delete this hour report?" }),
    primaryActionButtonLabel: t("actions.delete", { defaultValue: "Delete" }),
    onConfirm: (id) => {
      const report = reports.find((report) => report.id === id)
      if (!report?.editableAs) return
      return deleteHourReport(id, report.editableAs)
    },
  })
  const selectedMembership = memberships.find((membership) => membership.id === selectedMembershipId)
  const headerText = getTableHeaderText({
    appContext,
    fromDate,
    toDate,
    selectedMembershipName: selectedMembership?.fullname || selectedMembership?.userEmail,
    formatDate,
    t,
  })

  return (
    <Box>
      <Box display="flex" alignItems="stretch" flexWrap="wrap" style={{ gap: 16 }}>
        <DateRangeSelector
          fromDate={fromDate}
          toDate={toDate}
          firstDayOfWeek={calendarSettings.firstDayOfWeek}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          onDateRangeChange={setDateRange}
        />
        {!isUserContext ? (
          <ReporterMembershipFilter
            memberships={memberships}
            selectedMembershipId={selectedMembershipId}
            isLoading={isLoadingMemberships}
            onChange={setSelectedMembershipId}
          />
        ) : null}
      </Box>

      <Typography variant="h6" style={{ marginBottom: 16 }}>
        {headerText}
      </Typography>

      <Box className={classes.tableContainer}>
        {isLoadingReports || isLoadingCalendarSettings || isLoadingDayAudits ? (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <ApprovedHoursTable
            reports={reports}
            dayAudits={dayAudits}
            selectedMembershipId={selectedMembershipId ?? undefined}
            dateFormat={calendarSettings.dateFnsDateFormat}
            dateSeparator={calendarSettings.dateFormatSpecs.dateSeparator}
            firstDayOfWeek={calendarSettings.firstDayOfWeek}
            gridInfo={headerText}
            fromDate={fromDate}
            toDate={toDate}
            onAddHourReport={setAddHourReportDate}
            onEditHourReport={setEditingReportId}
            onDeleteHourReport={handleDeleteHourReport}
            workflowSettings={workflowSettings}
            isOrganisationContext={isOrganisationContext}
          />
        )}
      </Box>
      {addHourReportDate ? (
        <AddHourReportDialog
          date={addHourReportDate}
          selectedMembershipId={selectedMembershipId}
          workflowSettings={workflowSettings}
          onClose={() => setAddHourReportDate(null)}
        />
      ) : null}
      <HourReportCorrectionForm
        report={editingReport}
        editableAs={editingReport?.editableAs ?? null}
        setEditingReportId={setEditingReportId}
      />
    </Box>
  )
}

const useApproveHoursQueryParams = ({
  fromDate,
  toDate,
  appContext,
  membershipId,
}: UseApproveHoursQueryParamsOptions) => {
  return React.useMemo(() => {
    const queryParams: GetReportedHoursQuery = {
      fromDate: formatDateRangeQuery(fromDate),
      toDate: formatDateRangeQuery(toDate),
      ...makeReportedHoursScopeQuery({ appContext, membershipId }),
    }

    return queryParams
  }, [appContext, fromDate, membershipId, toDate])
}

const useDayAuditsQueryParams = ({ fromDate, toDate, membershipId }: UseApproveHoursQueryParamsOptions) => {
  return React.useMemo(() => {
    const queryParams: GetHourReportDayAuditsQuery = {
      fromDate: formatYMD(fromDate.toISOString()),
      toDate: formatYMD(toDate.toISOString()),
      membershipId: membershipId ?? "",
    }

    return queryParams
  }, [fromDate, membershipId, toDate])
}

const formatYMD = (isoDateString: string) => {
  const date = new Date(isoDateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableContainer: {
      position: "relative",
    },
    loadingOverlay: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: theme.spacing(14),
      background: theme.palette.background.paper,
      opacity: 0.7,
    },
  })
)

type UseApproveHoursQueryParamsOptions = {
  fromDate: Date
  toDate: Date
  appContext: ReturnType<typeof useAppContext>["appContext"]
  membershipId: string | null
}

export default ApproveHours
